import { toast } from "react-toastify";

/**
 * One pointer gesture must create one service.
 *
 * Editing a field and clicking "+ Novo serviço" blurs the input mid-press.
 * The list remaps, the chip unmounts, and a new one mounts under the cursor.
 * Chrome then delivers that same gesture to the new chip. Each insert shifts
 * the index, so a lock keyed by `insertAtIndex` accepts every iteration and
 * the "Serviço adicionado" toast loops.
 *
 * The lock is the category (supplier + type + calc option + group). Position
 * is not part of the key: the loop's signature is a new index on every pass.
 *
 * The toast lives here, not in the reducer. React may replay a reducer when a
 * render is interrupted; the replay starts from the same state, so the extra
 * call does not keep another row, but a toast inside the reducer still fires.
 * That showed "Serviço adicionado" again when nothing new stayed on screen.
 *
 * Pointer lock lives until a real pointerup. pointercancel is ignored: removing
 * the chip mid-press cancels the pointer while the button is still down, and
 * releasing there let the remounted chip accept another ADD_SERVICE.
 */

/** Double-click / retarget window. The flush hold covers the autosave gap. */
const WINDOW_MS = 400;

const SERVICE_ADDED_TOAST_ID = "service-added";

/** If pointerup is lost (unmount), do not keep the editor unclickable forever. */
const POINTER_LOCK_SAFETY_MS = 8000;

const lastAcceptedAtByKey = new Map<string, number>();

let pointerGestureLocked = false;
let gestureGeneration = 0;
let pointerRelease: ((event: Event) => void) | null = null;
let pointerSafetyTimer: number | null = null;

/**
 * Bumps on every hold. A save captures the epoch when it starts and only
 * releases holds from that epoch or earlier. A create accepted while the
 * request is in flight keeps its hold — the finishing save did not persist it,
 * and clearing it let the remounted control add again and toast.
 */
let holdEpoch = 0;

/** Category key → epoch at which that create was accepted. */
const heldUntilFlush = new Map<string, number>();

export type AddServiceGesturePayload = {
  supplierId?: string;
  type?: string;
  calcOption?: string;
  groupId?: string;
  insertAtIndex?: number;
};

export function addServiceGestureKey(
  payload: AddServiceGesturePayload | undefined
): string {
  return [
    payload?.supplierId ?? "",
    payload?.type ?? "",
    payload?.calcOption ?? "",
    payload?.groupId ?? "",
  ].join("|");
}

export function shouldAcceptAddService(
  payload: AddServiceGesturePayload | undefined,
  now = Date.now()
): boolean {
  const key = addServiceGestureKey(payload);
  if (heldUntilFlush.has(key)) {
    return false;
  }
  const last = lastAcceptedAtByKey.get(key);
  if (last != null && now - last < WINDOW_MS) {
    return false;
  }
  lastAcceptedAtByKey.set(key, now);
  return true;
}

/** Epoch a save must remember so its `finally` does not drop a newer hold. */
export function captureAddServiceFlushEpoch(): number {
  return holdEpoch;
}

/** Block another add in this category until the create POST has finished. */
export function holdAddServiceUntilFlush(
  payload: AddServiceGesturePayload | undefined
): void {
  holdEpoch += 1;
  heldUntilFlush.set(addServiceGestureKey(payload), holdEpoch);
}

/**
 * No argument clears every hold (tests, full reset).
 * An epoch releases only holds that already existed when that save started.
 */
export function releaseAddServiceFlushHolds(capturedEpoch?: number): void {
  if (capturedEpoch == null) {
    heldUntilFlush.clear();
    return;
  }
  heldUntilFlush.forEach((heldAt, key) => {
    if (heldAt <= capturedEpoch) heldUntilFlush.delete(key);
  });
}

/** One visible toast per gesture. A replayed reducer must not stack another. */
export function notifyServiceAdded(): void {
  if (
    typeof toast.isActive === "function" &&
    toast.isActive(SERVICE_ADDED_TOAST_ID)
  ) {
    return;
  }
  toast.success("Serviço adicionado", { toastId: SERVICE_ADDED_TOAST_ID });
}

/**
 * Single gate for every ADD_SERVICE, whichever store the screen uses.
 *
 * Orçamento goes through `activeBudgetDispatch`; the Serviços tab goes
 * through `activeServiceDispatch`. Those are different documents — this
 * helper does not merge the stores. It only makes the gesture lock the
 * same in both call sites.
 */
export function dispatchAddService<TAction>(
  dispatch: (action: TAction) => void,
  action: TAction & {
    type: "ADD_SERVICE";
    payload?: AddServiceGesturePayload;
  },
  options?: { holdUntilFlush?: boolean }
): boolean {
  if (!shouldAcceptAddService(action.payload)) return false;
  // Orçamento waits for the create POST. Serviços has no such flush — holding
  // until a budget autosave either never releases or releases on an unrelated
  // save and the toast fires again without a new row.
  if (options?.holdUntilFlush !== false) {
    holdAddServiceUntilFlush(action.payload);
  }
  dispatch(action);
  notifyServiceAdded();
  return true;
}

export function isAddServicePointerGestureLocked(): boolean {
  return pointerGestureLocked;
}

function finishPointerGesture(generation: number): void {
  if (generation !== gestureGeneration) return;
  pointerGestureLocked = false;
  if (pointerSafetyTimer != null && typeof window !== "undefined") {
    window.clearTimeout(pointerSafetyTimer);
    pointerSafetyTimer = null;
  }
}

function detachPointerRelease(): void {
  if (pointerRelease == null || typeof window === "undefined") return;
  window.removeEventListener("pointerup", pointerRelease, true);
  window.removeEventListener("pointercancel", pointerRelease, true);
  pointerRelease = null;
}

export function lockAddServicePointerGesture(): void {
  pointerGestureLocked = true;
  gestureGeneration += 1;
  const generation = gestureGeneration;
  if (typeof window === "undefined") return;

  detachPointerRelease();
  if (pointerSafetyTimer != null) {
    window.clearTimeout(pointerSafetyTimer);
    pointerSafetyTimer = null;
  }

  const release = (event: Event) => {
    if (generation !== gestureGeneration) return;
    // The chip unmounts under a still-pressed button. That cancel is not a
    // release — unlocking here is what let the new chip add again.
    if (event.type === "pointercancel") return;
    detachPointerRelease();
    window.setTimeout(() => finishPointerGesture(generation), 0);
  };

  pointerRelease = release;
  window.addEventListener("pointerup", release, true);
  window.addEventListener("pointercancel", release, true);
  pointerSafetyTimer = window.setTimeout(() => {
    finishPointerGesture(generation);
  }, POINTER_LOCK_SAFETY_MS);
}

type PointerGestureEvent = {
  button?: number;
  preventDefault: () => void;
  stopPropagation: () => void;
};

/**
 * Call at the start of the chip's mousedown, before any blur or add.
 * Returns false for a re-entrant call from the same press (the new chip
 * mounted under the cursor) so only the outer call inserts a service.
 *
 * preventDefault cancels the synthesized click. The focused field is blurred
 * explicitly so the pending edit still flushes.
 */
export function prepareAddServiceFromPointer(
  event: PointerGestureEvent
): boolean {
  if (event.button != null && event.button !== 0) return false;
  event.preventDefault();
  event.stopPropagation();
  if (pointerGestureLocked) return false;
  lockAddServicePointerGesture();

  const active =
    typeof document !== "undefined" ? document.activeElement : null;
  if (active instanceof HTMLElement && active !== document.body) {
    active.blur();
  }
  return true;
}

export function resetAddServiceGestures(): void {
  lastAcceptedAtByKey.clear();
  heldUntilFlush.clear();
  holdEpoch = 0;
  pointerGestureLocked = false;
  gestureGeneration += 1;
  detachPointerRelease();
  if (pointerSafetyTimer != null && typeof window !== "undefined") {
    window.clearTimeout(pointerSafetyTimer);
    pointerSafetyTimer = null;
  }
}
